import React from 'react'
import { cn } from '../../utils/cn'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'warning'
  size?: 'sm' | 'md' | 'lg'
  fullWidth?: boolean
  loading?: boolean
  isLoading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  as?: React.ElementType
  href?: string
  to?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  isLoading = false,
  leftIcon,
  rightIcon,
  className,
  disabled,
  as: Component = 'button',
  ...props
}, ref) => {
  const baseStyles = cn(
    'inline-flex items-center justify-center gap-2',
    'rounded-lg font-medium transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'select-none touch-manipulation', // Prevent text selection và enable touch manipulation
    'active:scale-95', // Touch feedback
    fullWidth && 'w-full',
    className
  )

  const variantStyles = {
    primary: cn(
      'bg-gradient-to-br from-primary-500 to-primary-600',
      'text-white',
      'hover:from-primary-600 hover:to-primary-700',
      'active:from-primary-700 active:to-primary-800',
      'focus:ring-primary-500',
      'shadow-md hover:shadow-lg'
    ),
    secondary: cn(
      'bg-secondary-100 dark:bg-secondary-800',
      'text-secondary-700 dark:text-secondary-300',
      'hover:bg-secondary-200 dark:hover:bg-secondary-700',
      'active:bg-secondary-300 dark:active:bg-secondary-600',
      'focus:ring-secondary-500',
      'shadow-sm hover:shadow'
    ),
    ghost: cn(
      'bg-transparent',
      'text-secondary-600 dark:text-secondary-400',
      'hover:bg-secondary-100 dark:hover:bg-secondary-800',
      'active:bg-secondary-200 dark:active:bg-secondary-700',
      'focus:ring-secondary-500'
    ),
    danger: cn(
      'bg-gradient-to-br from-error-500 to-error-600',
      'text-white',
      'hover:from-error-600 hover:to-error-700',
      'active:from-error-700 active:to-error-800',
      'focus:ring-error-500',
      'shadow-md hover:shadow-lg'
    ),
    success: cn(
      'bg-gradient-to-br from-success-500 to-success-600',
      'text-white',
      'hover:from-success-600 hover:to-success-700',
      'active:from-success-700 active:to-success-800',
      'focus:ring-success-500',
      'shadow-md hover:shadow-lg'
    ),
    warning: cn(
      'bg-gradient-to-br from-warning-500 to-warning-600',
      'text-white',
      'hover:from-warning-600 hover:to-warning-700',
      'active:from-warning-700 active:to-warning-800',
      'focus:ring-warning-500',
      'shadow-md hover:shadow-lg'
    ),
  }

  const sizeStyles = {
    sm: cn('px-3 py-1.5 text-sm min-h-[36px] min-w-[36px]'), // Minimum touch target 36px
    md: cn('px-4 py-2.5 text-base min-h-[44px] min-w-[44px]'), // Standard touch target 44px
    lg: cn('px-6 py-3 text-lg min-h-[52px] min-w-[44px]'), // Larger touch target
  }

  const isButtonLoading = loading || isLoading
  const loadingStyles = isButtonLoading ? 'relative text-transparent pointer-events-none' : ''

  return (
    <Component
      ref={ref}
      className={cn(
        baseStyles,
        variantStyles[variant],
        sizeStyles[size],
        loadingStyles,
        disabled && 'opacity-50 cursor-not-allowed'
      )}
      disabled={disabled || isButtonLoading}
      {...props}
    >
      {isButtonLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!isButtonLoading && leftIcon && <span className="w-5 h-5">{leftIcon}</span>}

      <span className={cn('truncate', isButtonLoading && 'invisible')}>
        {children}
      </span>

      {!isButtonLoading && rightIcon && <span className="w-5 h-5">{rightIcon}</span>}
    </Component>
  )
})

Button.displayName = 'Button'

// Pre-styled button variants for common use cases
export const PrimaryButton = React.forwardRef<HTMLButtonElement, Omit<ButtonProps, 'variant'>>(
  (props, ref) => <Button ref={ref} variant="primary" {...props} />
)

export const SecondaryButton = React.forwardRef<HTMLButtonElement, Omit<ButtonProps, 'variant'>>(
  (props, ref) => <Button ref={ref} variant="secondary" {...props} />
)

export const GhostButton = React.forwardRef<HTMLButtonElement, Omit<ButtonProps, 'variant'>>(
  (props, ref) => <Button ref={ref} variant="ghost" {...props} />
)

export const DangerButton = React.forwardRef<HTMLButtonElement, Omit<ButtonProps, 'variant'>>(
  (props, ref) => <Button ref={ref} variant="danger" {...props} />
)

// Icon Button - square button for icons
interface IconButtonProps extends Omit<ButtonProps, 'children' | 'leftIcon' | 'rightIcon'> {
  icon: React.ReactNode
  label: string
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(({
  icon,
  label,
  size = 'md',
  ...props
}, ref) => {
  const sizeMap = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-3',
  }

  return (
    <Button
      ref={ref}
      size={size}
      aria-label={label}
      className={cn(
        sizeMap[size],
        'aspect-square' // Ensure square aspect ratio
      )}
      {...props}
    >
      <span className="w-5 h-5 flex items-center justify-center">{icon}</span>
    </Button>
  )
})

export default Button
